import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { useCampaignBusinessDrivers } from './useCampaignBusinessDrivers';
import type { z } from 'zod';
import type { crmCampaignCommissioningFieldsSchema } from '@easyflow/shared';

export type CampaignCommissioningValues = z.infer<typeof crmCampaignCommissioningFieldsSchema>;
export type CampaignCommissioningForm = {
  business_driver_id: NonNullable<CampaignCommissioningValues['business_driver_id']>;
  commissioning_customer_list_required: NonNullable<CampaignCommissioningValues['commissioning_customer_list_required']>;
};

interface Props {
  open: boolean;
  campaignId: string | undefined;
  value: CampaignCommissioningForm;
  onChange: (value: CampaignCommissioningForm) => void;
}

export function CampaignCommissioningFields({ open, campaignId, value, onChange }: Props) {
  const { state, retry } = useCampaignBusinessDrivers(open, campaignId);
  const drivers = state.status === 'ready' ? state.drivers : [];
  const activeDrivers = drivers.filter(driver => driver.is_active !== false);
  const selected = drivers.find(driver => driver.id === value.business_driver_id);
  const preserved = !!value.business_driver_id && (!selected || selected.is_active === false);
  const preservedLabel = selected?.is_active === false
    ? `${selected.name || value.business_driver_id} — inattivo`
    : `${value.business_driver_id} — ${state.status === 'ready' ? 'non disponibile' : 'selezione conservata'}`;

  return (
    <div className="space-y-3 border-t pt-4">
      <div>
        <Label>Configurazione commissioning</Label>
        <p className="text-xs text-muted-foreground">
          Collega la campagna a un driver commerciale: gli eventi CRM eleggibili (lead vinto,
          conversione, azione massiva…) alimentano le gare Incentivazione basate su quel driver.
          Si contano gli eventi eleggibili, non i contatti unici né i segmenti di targeting.
        </p>
      </div>
      <div>
        <Label htmlFor="campaign-business-driver" className="text-xs">Driver commerciale</Label>
        <Select value={value.business_driver_id || '__none__'}
          onValueChange={id => onChange({ ...value, business_driver_id: id === '__none__' ? '' : id })}>
          <SelectTrigger id="campaign-business-driver" aria-describedby="campaign-driver-status">
            <SelectValue placeholder="Nessun driver (commissioning disattivo)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Nessun driver (commissioning disattivo)</SelectItem>
            {preserved && <SelectItem value={value.business_driver_id} disabled>{preservedLabel}</SelectItem>}
            {activeDrivers.map(driver => (
              <SelectItem key={driver.id} value={driver.id}>{driver.name || driver.id}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div id="campaign-driver-status" aria-live="polite" className="text-xs text-muted-foreground mt-1 space-y-1">
          {state.status === 'loading' && <div role="status">
            <div aria-hidden="true" className="h-2 w-32 rounded bg-muted animate-pulse mb-1" />
            Caricamento driver commerciali…
          </div>}
          {state.status === 'ready' && activeDrivers.length === 0 && (
            <p>Nessun driver attivo disponibile nel Catalogo POS.</p>
          )}
          {(state.status === 'forbidden' || state.status === 'error') && <div role="alert">
            <p>{state.status === 'forbidden'
              ? 'Catalogo POS non accessibile: verifica i permessi o l’attivazione del modulo con un amministratore.'
              : 'Caricamento driver non riuscito.'}</p>
            <p>{state.message}</p>
            <Button type="button" variant="outline" size="sm" className="mt-1" onClick={retry}>Riprova caricamento driver</Button>
          </div>}
          {preserved && <p>
            Driver selezionato: {preservedLabel}. La selezione resta nella bozza finché non la cambi esplicitamente.
          </p>}
        </div>
      </div>
      <div className="flex items-center justify-between gap-3">
        <div>
          <Label className="text-xs">Lista clienti richiesta</Label>
          <p className="text-xs text-muted-foreground">
            Le gare collegate contano solo gli eventi eleggibili dei clienti presenti nella lista di riferimento della campagna.
          </p>
        </div>
        <Switch aria-label="Lista clienti richiesta per le gare"
          checked={value.commissioning_customer_list_required}
          onCheckedChange={checked => onChange({ ...value, commissioning_customer_list_required: checked })}
          disabled={!value.business_driver_id} />
      </div>
    </div>
  );
}
